/**
 * Copyright Highway9 Networks Inc.
 */

import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "..";
import memoryService from "~/services/memory-service";
import moment from "moment-timezone";
import { ChatMemory } from "~/types/chatMemory";

export interface MemoryState {
  memories: ChatMemory[];
  filteredMemories: ChatMemory[];
  searchTerm: string;
  categoryFilter: string; // "ALL" | "RADIO" | "EDGE" | "DEVICES"
  loading: boolean;
  error: string | null;
  expandedMemoryIds: string[];
  editingMemoryId: string | null;
}

const initialState: MemoryState = {
  memories: [],
  filteredMemories: [],
  searchTerm: "",
  categoryFilter: "ALL",
  loading: false,
  error: null,
  expandedMemoryIds: [],
  editingMemoryId: null,
};

// Async thunks for API operations
export const fetchMemories = createAsyncThunk(
  "memory/fetchMemories",
  async (_, { rejectWithValue }) => {
    try {
      return await memoryService.fetchMemories();
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch memories");
    }
  }
);

export const createMemory = createAsyncThunk(
  "memory/createMemory",
  async (
    memory: Omit<ChatMemory, "id" | "creationTime" | "updatedTime" | "owner">,
    { rejectWithValue }
  ) => {
    try {
      return await memoryService.createMemory(memory as ChatMemory);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create memory");
    }
  }
);

export const updateMemory = createAsyncThunk(
  "memory/updateMemory",
  async (
    { id, updates }: { id: string; updates: Partial<ChatMemory> },
    { rejectWithValue }
  ) => {
    try {
      return await memoryService.updateMemory(updates as ChatMemory , id);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update memory");
    }
  }
);

export const deleteMemory = createAsyncThunk(
  "memory/deleteMemory",
  async (id: string, { rejectWithValue }) => {
    try {
      await memoryService.deleteMemory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete memory");
    }
  }
);

// Helper function to filter memories
const filterMemories = (
  memories: ChatMemory[],
  searchTerm: string,
  categoryFilter: string
): ChatMemory[] => {
  let filtered = memories;

  // Filter by category
  if (categoryFilter !== "ALL") {
    filtered = filtered.filter((memory) => memory.category === categoryFilter);
  }

  // Filter by search term
  if (searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase().trim();
    filtered = filtered.filter(
      (memory) =>
        memory.title.toLowerCase().includes(searchLower) ||
        memory.content.toLowerCase().includes(searchLower)
    );
  }

  // Sort by creation date (newest first)
  return filtered.sort(
    (a, b) => new Date(b.creationTime).getTime() - new Date(a.creationTime).getTime()
  );
};

const memorySlice = createSlice({
  name: "memory",
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.filteredMemories = filterMemories(
        state.memories,
        action.payload,
        state.categoryFilter
      );
    },
    setCategoryFilter: (state, action: PayloadAction<string>) => {
      state.categoryFilter = action.payload;
      state.filteredMemories = filterMemories(
        state.memories,
        state.searchTerm,
        action.payload
      );
    },
    clearFilters: (state) => {
      state.searchTerm = "";
      state.categoryFilter = "ALL";
      state.filteredMemories = filterMemories(state.memories, "", "ALL");
    },
    toggleMemoryExpanded: (state, action: PayloadAction<string>) => {
      const memoryId = action.payload;
      const index = state.expandedMemoryIds.indexOf(memoryId);
      if (index >= 0) {
        state.expandedMemoryIds.splice(index, 1);
      } else {
        state.expandedMemoryIds.push(memoryId);
      }
    },
    setEditingMemory: (state, action: PayloadAction<string | null>) => {
      state.editingMemoryId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Optimistic update for better UX
    optimisticUpdateMemory: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<ChatMemory> }>
    ) => {
      const { id, updates } = action.payload;
      const index = state.memories.findIndex((memory) => memory.id === id);
      if (index >= 0) {
        state.memories[index] = {
          ...state.memories[index],
          ...updates,
          updatedTime: moment().unix(),
        };
        state.filteredMemories = filterMemories(
          state.memories,
          state.searchTerm,
          state.categoryFilter
        );
      }
    },
    // Optimistic delete for better UX
    optimisticDeleteMemory: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.memories = state.memories.filter((memory) => memory.id !== id);
      state.filteredMemories = state.filteredMemories.filter(
        (memory) => memory.id !== id
      );
      state.expandedMemoryIds = state.expandedMemoryIds.filter(
        (memoryId) => memoryId !== id
      );
      if (state.editingMemoryId === id) {
        state.editingMemoryId = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch memories
    builder
      .addCase(fetchMemories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMemories.fulfilled, (state, action) => {
        state.loading = false;
        state.memories = action.payload;
        state.filteredMemories = filterMemories(
          action.payload,
          state.searchTerm,
          state.categoryFilter
        );
      })
      .addCase(fetchMemories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create memory
    builder
      .addCase(createMemory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMemory.fulfilled, (state, action) => {
        state.loading = false;
        state.memories.unshift(action.payload); // Add to beginning
        state.filteredMemories = filterMemories(
          state.memories,
          state.searchTerm,
          state.categoryFilter
        );
      })
      .addCase(createMemory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update memory
    builder
      .addCase(updateMemory.pending, (state) => {
        state.error = null;
      })
      .addCase(updateMemory.fulfilled, (state, action) => {
        const index = state.memories.findIndex(
          (memory) => memory.id === action.payload.id
        );
        if (index >= 0) {
          state.memories[index] = action.payload;
          state.filteredMemories = filterMemories(
            state.memories,
            state.searchTerm,
            state.categoryFilter
          );
        }
        state.editingMemoryId = null;
      })
      .addCase(updateMemory.rejected, (state, action) => {
        state.error = action.payload as string;
        // Revert optimistic update if needed
        // This would require storing the original state, which we'll handle in the component
      });

    // Delete memory
    builder
      .addCase(deleteMemory.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteMemory.fulfilled, (state, action) => {
        // Memory already removed by optimistic update
        const deletedId = action.payload;
        // Ensure it's removed (in case optimistic update failed)
        state.memories = state.memories.filter(
          (memory) => memory.id !== deletedId
        );
        state.filteredMemories = state.filteredMemories.filter(
          (memory) => memory.id !== deletedId
        );
      })
      .addCase(deleteMemory.rejected, (state, action) => {
        state.error = action.payload as string;
        // For delete failures, we should restore the memory
        // This would require storing the deleted memory, which we'll handle in the component
      });
  },
});

export const {
  setSearchTerm,
  setCategoryFilter,
  clearFilters,
  toggleMemoryExpanded,
  setEditingMemory,
  clearError,
  optimisticUpdateMemory,
  optimisticDeleteMemory,
} = memorySlice.actions;

export default memorySlice.reducer;

// Selectors
export const memoryData = (state: RootState) => state.memory.memories;
export const memoryFiltered = (state: RootState) => state.memory.filteredMemories;
export const memorySearchTerm = (state: RootState) => state.memory.searchTerm;
export const memoryCategoryFilter = (state: RootState) => state.memory.categoryFilter;
export const memoryLoading = (state: RootState) => state.memory.loading;
export const memoryError = (state: RootState) => state.memory.error;
export const memoryExpandedIds = (state: RootState) => state.memory.expandedMemoryIds;
export const memoryEditingId = (state: RootState) =>
  state.memory.editingMemoryId;

// Complex selectors
export const selectIsMemoryExpanded = (memoryId: string) => (state: RootState) =>
  state.memory.expandedMemoryIds.includes(memoryId);

export const selectIsMemoryEditing = (memoryId: string) => (state: RootState) =>
  state.memory.editingMemoryId === memoryId;

export const selectMemoryById = (memoryId: string) => (state: RootState) =>
  state.memory.memories.find((memory) => memory.id === memoryId);

export const selectMemoryStatistics = (state: RootState) => {
  const memories = state.memory.memories;
  return {
    total: memories.length,
    byCategory: {
      RADIO: memories.filter((m) => m.category === "RADIO").length,
      EDGE: memories.filter((m) => m.category === "EDGE").length,
      DEVICES: memories.filter((m) => m.category === "DEVICES").length,
    },
    filtered: state.memory.filteredMemories.length,
  };
}; 